import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Init data
  units: [],
  types: [],
  statuses: [],
  marks: [],
  initLoading: false,
  initError: null,
  // Backup config data
  backupConfig: null,
  backupConfigLoading: false,
  backupConfigError: null,
  // Save backup config
  saveLoading: false,
  saveError: null,
  saveSuccess: false,
  saveSuccessMessage: null,
  // Manual backup
  manualBackupLoading: false,
  manualBackupError: null,
  manualBackupSuccess: false,
  manualBackupSuccessMessage: null,
  // Backup logs
  backupLogs: [],
  backupLogsLoading: false,
  backupLogsError: null,
  backupLogsTotal: 0,
  backupLogsPage: 1,
  backupLogsSize: 10,
  // Backup logs filters
  filterUnitIds: [],
  filterTypes: [],
  filterStatuses: [],
  filterIsMarks: [],
  filterFromDate: null,
  filterToDate: null,
  // Mark backup log
  markLoading: false,
  markError: null,
  markSuccess: false,
  markSuccessMessage: null,
  // Restore backup log
  restoreLoading: false,
  restoreError: null,
  restoreSuccess: false,
  restoreSuccessMessage: null,
  // Restore logs (Tab 3)
  restoreLogs: [],
  restoreLogsLoading: false,
  restoreLogsError: null,
  restoreLogsTotal: 0,
  restoreLogsPage: 1,
  restoreLogsSize: 10,
  // Restore logs filters (no isMark filter)
  filterRestoreUnitIds: [],
  filterRestoreTypes: [],
  filterRestoreStatuses: [],
  filterRestoreFromDate: null,
  filterRestoreToDate: null,
};

const syncDataSlice = createSlice({
  name: 'syncData',
  initialState,
  reducers: {
    // Fetch init data
    fetchInitRequest: (state) => {
      state.initLoading = true;
      state.initError = null;
    },
    fetchInitSuccess: (state, action) => {
      state.initLoading = false;
      state.units = action.payload.units || [];
      state.types = action.payload.types || [];
      state.statuses = action.payload.statuses || [];
      state.marks = action.payload.marks || [];
      state.initError = null;
    },
    fetchInitFailure: (state, action) => {
      state.initLoading = false;
      state.initError = action.payload;
    },
    // Fetch backup config
    fetchBackupConfigRequest: (state) => {
      state.backupConfigLoading = true;
      state.backupConfigError = null;
    },
    fetchBackupConfigSuccess: (state, action) => {
      state.backupConfigLoading = false;
      state.backupConfig = action.payload;
      state.backupConfigError = null;
    },
    fetchBackupConfigFailure: (state, action) => {
      state.backupConfigLoading = false;
      state.backupConfig = null;
      state.backupConfigError = action.payload;
    },
    // Clear backup config
    clearBackupConfig: (state) => {
      state.backupConfig = null;
      state.backupConfigError = null;
    },
    // Save backup config
    saveBackupConfigRequest: (state) => {
      state.saveLoading = true;
      state.saveError = null;
      state.saveSuccess = false;
    },
    saveBackupConfigSuccess: (state, action) => {
      state.saveLoading = false;
      state.saveSuccess = true;
      state.saveSuccessMessage = action.payload?.message || null;
      state.saveError = null;
    },
    saveBackupConfigFailure: (state, action) => {
      state.saveLoading = false;
      state.saveSuccess = false;
      state.saveError = action.payload;
    },
    // Clear save status
    clearSaveStatus: (state) => {
      state.saveSuccess = false;
      state.saveSuccessMessage = null;
      state.saveError = null;
    },
    // Manual backup
    manualBackupRequest: (state) => {
      state.manualBackupLoading = true;
      state.manualBackupError = null;
      state.manualBackupSuccess = false;
    },
    manualBackupSuccess: (state, action) => {
      state.manualBackupLoading = false;
      state.manualBackupSuccess = true;
      state.manualBackupSuccessMessage = action.payload?.message || null;
      state.manualBackupError = null;
    },
    manualBackupFailure: (state, action) => {
      state.manualBackupLoading = false;
      state.manualBackupSuccess = false;
      state.manualBackupError = action.payload;
    },
    // Clear manual backup status
    clearManualBackupStatus: (state) => {
      state.manualBackupSuccess = false;
      state.manualBackupSuccessMessage = null;
      state.manualBackupError = null;
    },
    // Fetch backup logs
    fetchBackupLogsRequest: (state, action) => {
      state.backupLogsLoading = true;
      state.backupLogsError = null;
      if (action.payload?.page) {
        state.backupLogsPage = action.payload.page;
      }
      if (action.payload?.size) {
        state.backupLogsSize = action.payload.size;
      }
    },
    fetchBackupLogsSuccess: (state, action) => {
      state.backupLogsLoading = false;
      state.backupLogs = action.payload.data || [];
      state.backupLogsTotal = action.payload.meta?.total || 0;
      state.backupLogsError = null;
    },
    fetchBackupLogsFailure: (state, action) => {
      state.backupLogsLoading = false;
      state.backupLogsError = action.payload;
    },
    // Set backup logs page
    setBackupLogsPage: (state, action) => {
      state.backupLogsPage = action.payload;
    },
    // Set backup logs page size
    setBackupLogsSize: (state, action) => {
      state.backupLogsSize = action.payload;
      state.backupLogsPage = 1;
    },
    // Set backup logs filters
    setFilterUnitIds: (state, action) => {
      state.filterUnitIds = action.payload;
      state.backupLogsPage = 1;
    },
    setFilterTypes: (state, action) => {
      state.filterTypes = action.payload;
      state.backupLogsPage = 1;
    },
    setFilterStatuses: (state, action) => {
      state.filterStatuses = action.payload;
      state.backupLogsPage = 1;
    },
    setFilterIsMarks: (state, action) => {
      state.filterIsMarks = action.payload;
      state.backupLogsPage = 1;
    },
    setFilterFromDate: (state, action) => {
      state.filterFromDate = action.payload;
      state.backupLogsPage = 1;
    },
    setFilterToDate: (state, action) => {
      state.filterToDate = action.payload;
      state.backupLogsPage = 1;
    },
    clearFilters: (state) => {
      state.filterUnitIds = [];
      state.filterTypes = [];
      state.filterStatuses = [];
      state.filterIsMarks = [];
      state.filterFromDate = null;
      state.filterToDate = null;
      state.backupLogsPage = 1;
    },
    // Mark backup log
    markBackupLogRequest: (state) => {
      state.markLoading = true;
      state.markError = null;
      state.markSuccess = false;
    },
    markBackupLogSuccess: (state, action) => {
      state.markLoading = false;
      state.markSuccess = true;
      state.markSuccessMessage = action.payload?.message || null;
      state.markError = null;
    },
    markBackupLogFailure: (state, action) => {
      state.markLoading = false;
      state.markSuccess = false;
      state.markError = action.payload;
    },
    clearMarkStatus: (state) => {
      state.markSuccess = false;
      state.markSuccessMessage = null;
      state.markError = null;
    },
    // Restore backup log
    restoreBackupLogRequest: (state) => {
      state.restoreLoading = true;
      state.restoreError = null;
      state.restoreSuccess = false;
    },
    restoreBackupLogSuccess: (state, action) => {
      state.restoreLoading = false;
      state.restoreSuccess = true;
      state.restoreSuccessMessage = action.payload?.message || null;
      state.restoreError = null;
    },
    restoreBackupLogFailure: (state, action) => {
      state.restoreLoading = false;
      state.restoreSuccess = false;
      state.restoreError = action.payload;
    },
    clearRestoreStatus: (state) => {
      state.restoreSuccess = false;
      state.restoreSuccessMessage = null;
      state.restoreError = null;
    },
    // Fetch restore logs (Tab 3)
    fetchRestoreLogsRequest: (state, action) => {
      state.restoreLogsLoading = true;
      state.restoreLogsError = null;
      if (action.payload?.page) {
        state.restoreLogsPage = action.payload.page;
      }
      if (action.payload?.size) {
        state.restoreLogsSize = action.payload.size;
      }
    },
    fetchRestoreLogsSuccess: (state, action) => {
      state.restoreLogsLoading = false;
      state.restoreLogs = action.payload.data || [];
      state.restoreLogsTotal = action.payload.meta?.total || 0;
      state.restoreLogsError = null;
    },
    fetchRestoreLogsFailure: (state, action) => {
      state.restoreLogsLoading = false;
      state.restoreLogsError = action.payload;
    },
    // Set restore logs page
    setRestoreLogsPage: (state, action) => {
      state.restoreLogsPage = action.payload;
    },
    // Set restore logs page size
    setRestoreLogsSize: (state, action) => {
      state.restoreLogsSize = action.payload;
      state.restoreLogsPage = 1;
    },
    // Set restore logs filters
    setFilterRestoreUnitIds: (state, action) => {
      state.filterRestoreUnitIds = action.payload;
      state.restoreLogsPage = 1;
    },
    setFilterRestoreTypes: (state, action) => {
      state.filterRestoreTypes = action.payload;
      state.restoreLogsPage = 1;
    },
    setFilterRestoreStatuses: (state, action) => {
      state.filterRestoreStatuses = action.payload;
      state.restoreLogsPage = 1;
    },
    setFilterRestoreFromDate: (state, action) => {
      state.filterRestoreFromDate = action.payload;
      state.restoreLogsPage = 1;
    },
    setFilterRestoreToDate: (state, action) => {
      state.filterRestoreToDate = action.payload;
      state.restoreLogsPage = 1;
    },
    clearRestoreLogsFilters: (state) => {
      state.filterRestoreUnitIds = [];
      state.filterRestoreTypes = [];
      state.filterRestoreStatuses = [];
      state.filterRestoreFromDate = null;
      state.filterRestoreToDate = null;
      state.restoreLogsPage = 1;
    },
  },
});

export const {
  fetchInitRequest,
  fetchInitSuccess,
  fetchInitFailure,
  fetchBackupConfigRequest,
  fetchBackupConfigSuccess,
  fetchBackupConfigFailure,
  clearBackupConfig,
  saveBackupConfigRequest,
  saveBackupConfigSuccess,
  saveBackupConfigFailure,
  clearSaveStatus,
  manualBackupRequest,
  manualBackupSuccess,
  manualBackupFailure,
  clearManualBackupStatus,
  fetchBackupLogsRequest,
  fetchBackupLogsSuccess,
  fetchBackupLogsFailure,
  setBackupLogsPage,
  setBackupLogsSize,
  setFilterUnitIds,
  setFilterTypes,
  setFilterStatuses,
  setFilterIsMarks,
  setFilterFromDate,
  setFilterToDate,
  clearFilters,
  markBackupLogRequest,
  markBackupLogSuccess,
  markBackupLogFailure,
  clearMarkStatus,
  restoreBackupLogRequest,
  restoreBackupLogSuccess,
  restoreBackupLogFailure,
  clearRestoreStatus,
  // Restore logs (Tab 3)
  fetchRestoreLogsRequest,
  fetchRestoreLogsSuccess,
  fetchRestoreLogsFailure,
  setRestoreLogsPage,
  setRestoreLogsSize,
  setFilterRestoreUnitIds,
  setFilterRestoreTypes,
  setFilterRestoreStatuses,
  setFilterRestoreFromDate,
  setFilterRestoreToDate,
  clearRestoreLogsFilters,
} = syncDataSlice.actions;

// Selectors
export const selectUnits = (state) => state.syncData?.units || [];
export const selectTypes = (state) => state.syncData?.types || [];
export const selectStatuses = (state) => state.syncData?.statuses || [];
export const selectMarks = (state) => state.syncData?.marks || [];
export const selectInitLoading = (state) => state.syncData?.initLoading || false;
export const selectInitError = (state) => state.syncData?.initError;
export const selectBackupConfig = (state) => state.syncData?.backupConfig;
export const selectBackupConfigLoading = (state) => state.syncData?.backupConfigLoading || false;
export const selectBackupConfigError = (state) => state.syncData?.backupConfigError;
export const selectSaveLoading = (state) => state.syncData?.saveLoading || false;
export const selectSaveError = (state) => state.syncData?.saveError;
export const selectSaveSuccess = (state) => state.syncData?.saveSuccess || false;
export const selectSaveSuccessMessage = (state) => state.syncData?.saveSuccessMessage;
export const selectManualBackupLoading = (state) => state.syncData?.manualBackupLoading || false;
export const selectManualBackupError = (state) => state.syncData?.manualBackupError;
export const selectManualBackupSuccess = (state) => state.syncData?.manualBackupSuccess || false;
export const selectManualBackupSuccessMessage = (state) => state.syncData?.manualBackupSuccessMessage;
// Backup logs selectors
export const selectBackupLogs = (state) => state.syncData?.backupLogs || [];
export const selectBackupLogsLoading = (state) => state.syncData?.backupLogsLoading || false;
export const selectBackupLogsError = (state) => state.syncData?.backupLogsError;
export const selectBackupLogsTotal = (state) => state.syncData?.backupLogsTotal || 0;
export const selectBackupLogsPage = (state) => state.syncData?.backupLogsPage || 1;
export const selectBackupLogsSize = (state) => state.syncData?.backupLogsSize || 10;
// Filter selectors
export const selectFilterUnitIds = (state) => state.syncData?.filterUnitIds || [];
export const selectFilterTypes = (state) => state.syncData?.filterTypes || [];
export const selectFilterStatuses = (state) => state.syncData?.filterStatuses || [];
export const selectFilterIsMarks = (state) => state.syncData?.filterIsMarks || [];
export const selectFilterFromDate = (state) => state.syncData?.filterFromDate;
export const selectFilterToDate = (state) => state.syncData?.filterToDate;
// Mark backup log selectors
export const selectMarkLoading = (state) => state.syncData?.markLoading || false;
export const selectMarkError = (state) => state.syncData?.markError;
export const selectMarkSuccess = (state) => state.syncData?.markSuccess || false;
export const selectMarkSuccessMessage = (state) => state.syncData?.markSuccessMessage;
// Restore backup log selectors
export const selectRestoreLoading = (state) => state.syncData?.restoreLoading || false;
export const selectRestoreError = (state) => state.syncData?.restoreError;
export const selectRestoreSuccess = (state) => state.syncData?.restoreSuccess || false;
export const selectRestoreSuccessMessage = (state) => state.syncData?.restoreSuccessMessage;
// Restore logs selectors (Tab 3)
export const selectRestoreLogs = (state) => state.syncData?.restoreLogs || [];
export const selectRestoreLogsLoading = (state) => state.syncData?.restoreLogsLoading || false;
export const selectRestoreLogsError = (state) => state.syncData?.restoreLogsError;
export const selectRestoreLogsTotal = (state) => state.syncData?.restoreLogsTotal || 0;
export const selectRestoreLogsPage = (state) => state.syncData?.restoreLogsPage || 1;
export const selectRestoreLogsSize = (state) => state.syncData?.restoreLogsSize || 10;
// Restore logs filter selectors
export const selectFilterRestoreUnitIds = (state) => state.syncData?.filterRestoreUnitIds || [];
export const selectFilterRestoreTypes = (state) => state.syncData?.filterRestoreTypes || [];
export const selectFilterRestoreStatuses = (state) => state.syncData?.filterRestoreStatuses || [];
export const selectFilterRestoreFromDate = (state) => state.syncData?.filterRestoreFromDate;
export const selectFilterRestoreToDate = (state) => state.syncData?.filterRestoreToDate;

export default syncDataSlice.reducer;
